import { useEffect, useState, useCallback, useMemo } from "react";
import { Typography, Spin, Modal, Select } from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    PlusOutlined,
    SearchOutlined,
    ReloadOutlined,
    MedicineBoxOutlined,
} from "@ant-design/icons";
import ApiClient from "../api";

const api_client = new ApiClient();

// ─────────────────────────────────────────────────────────────────────────────
// Constants & helpers
// ─────────────────────────────────────────────────────────────────────────────

const { Text } = Typography;

const SPECIALTY_OPTIONS = [
    'CARDIOLOGIST',
    'PAEDIATRICIAN',
    'SPECIALIST',
    'SURGEON',
    'ANAESTHETIST',
    'PSYCHIATRIST',
];

const SPECIALTY_PALETTE = [
    { bg: 'rgba(121,167,165,0.15)', text: '#79A7A5', border: 'rgba(121,167,165,0.3)' },
    { bg: 'rgba(250,189,88,0.12)',  text: '#FABB44', border: 'rgba(250,189,88,0.25)' },
    { bg: 'rgba(129,140,248,0.12)', text: '#A5B4FC', border: 'rgba(129,140,248,0.25)' },
    { bg: 'rgba(244,114,182,0.12)', text: '#F9A8D4', border: 'rgba(244,114,182,0.25)' },
    { bg: 'rgba(74,222,128,0.12)',  text: '#4ADE80', border: 'rgba(74,222,128,0.25)' },
    { bg: 'rgba(148,163,184,0.1)',  text: '#94A3B8', border: 'rgba(148,163,184,0.2)' },
];

const getSpecialtyStyle = (specialty = '') => {
    let hash = 0;
    for (let i = 0; i < specialty.length; i++) hash = (hash * 31 + specialty.charCodeAt(i)) >>> 0;
    return SPECIALTY_PALETTE[hash % SPECIALTY_PALETTE.length];
};

const cleanName = (name = '') =>
    name.replace(/\b(DR|MR|MRS|MS|MISS|PROF)\b\.?/gi, '').replace(/\s+/g, ' ').trim();

const getInitials = (name = '') => {
    const parts = cleanName(name).split(' ').filter(Boolean);
    if (!parts.length) return '?';
    return parts.length === 1
        ? parts[0].slice(0, 2).toUpperCase()
        : `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────

const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    background: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
};

const labelStyle = {
    display: 'block',
    color: 'var(--text-secondary, #94A3B8)',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    marginBottom: 5,
};

const btnBase = {
    padding: '8px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    transition: 'opacity 0.15s',
};

// ─────────────────────────────────────────────────────────────────────────────
// Toast
// ─────────────────────────────────────────────────────────────────────────────

const Toast = ({ toasts }) => (
    <div style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 10,
        pointerEvents: 'none',
    }}>
        {toasts.map(t => (
            <div key={t.id} style={{
                padding: '12px 18px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 500,
                background: t.type === 'error' ? 'rgba(239,68,68,0.12)' : 'rgba(74,222,128,0.1)',
                border: `1px solid ${t.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(74,222,128,0.3)'}`,
                color: t.type === 'error' ? '#EF4444' : '#4ADE80',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                animation: 'fadeSlideIn 0.25s ease',
                maxWidth: 320,
            }}>
                {t.message}
            </div>
        ))}
    </div>
);

const useToast = () => {
    const [toasts, setToasts] = useState([]);
    const push = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    }, []);
    return { toasts, push };
};

// ─────────────────────────────────────────────────────────────────────────────
// Field — defined outside modals to prevent remount on each keystroke
// ─────────────────────────────────────────────────────────────────────────────

const Field = ({ label, fieldKey, type = 'text', placeholder, value, error, onChange, autoFocus }) => (
    <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>{label}</label>
        <input
            type={type}
            value={value}
            autoFocus={autoFocus}
            onChange={e => onChange(fieldKey, e.target.value)}
            placeholder={placeholder}
            style={{
                ...inputStyle,
                borderColor: error ? '#EF4444' : 'var(--border-default)',
            }}
            onFocus={e => { if (!error) e.target.style.borderColor = '#79A7A5'; }}
            onBlur={e  => { if (!error) e.target.style.borderColor = 'var(--border-default)'; }}
        />
        {error && (
            <span style={{ color: '#EF4444', fontSize: 11, marginTop: 4, display: 'block' }}>
                {error}
            </span>
        )}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Add / Edit Doctor Modal — shared for both create and update
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FORM = { name: '', specialty: '' };

const DoctorFormModal = ({ open, mode, doctor, loading, onConfirm, onCancel }) => {
    const [form,   setForm]   = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});

    const isEdit = mode === 'edit';

    useEffect(() => {
        if (open) {
            setForm(isEdit && doctor ? { name: doctor.name || '', specialty: doctor.specialty || '' } : EMPTY_FORM);
            setErrors({});
        }
    }, [open, isEdit, doctor]);

    const set = useCallback((key, val) => {
        setForm(prev => ({ ...prev, [key]: val }));
        setErrors(prev => ({ ...prev, [key]: '' }));
    }, []);

    const validate = () => {
        const e = {};
        if (!form.name.trim())      e.name      = 'Name is required';
        if (!form.specialty.trim()) e.specialty = 'Specialty is required';
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        onConfirm({ name: form.name.trim(), specialty: form.specialty.trim() });
    };

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            centered
            width={440}
            styles={{
                content: {
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 16,
                    padding: 0,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                },
            }}
        >
            <div style={{ padding: '28px 28px 24px' }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 11,
                        background: 'rgba(121,167,165,0.12)', border: '1px solid rgba(121,167,165,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <MedicineBoxOutlined style={{ color: '#79A7A5', fontSize: 18 }} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 16 }}>
                            {isEdit ? 'Edit Doctor' : 'Add New Doctor'}
                        </div>
                        <div style={{ color: 'var(--text-secondary, #94A3B8)', fontSize: 12 }}>
                            {isEdit ? 'Update name or specialty' : 'Register a doctor in the system'}
                        </div>
                    </div>
                </div>

                <Field
                    label="Doctor Name"
                    fieldKey="name"
                    placeholder="e.g. Dr. John Doe"
                    value={form.name}
                    error={errors.name}
                    onChange={set}
                    autoFocus
                />
                <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Specialty</label>
                    <Select
                        value={form.specialty || undefined}
                        onChange={(val) => set('specialty', val)}
                        placeholder="Select specialty"
                        style={{ width: '100%' }}
                        status={errors.specialty ? 'error' : undefined}
                        options={SPECIALTY_OPTIONS.map(s => ({ label: s, value: s }))}
                    />
                    {errors.specialty && (
                        <span style={{ color: '#EF4444', fontSize: 11, marginTop: 4, display: 'block' }}>
                            {errors.specialty}
                        </span>
                    )}
                </div>

                <div style={{ height: 1, background: 'var(--border-default)', margin: '4px 0 20px' }} />

                {/* Footer */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        style={{
                            ...btnBase,
                            border: '1px solid var(--border-default)',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            ...btnBase,
                            background: '#79A7A5',
                            color: '#0D0F14',
                            fontWeight: 700,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        {loading && <Spin size="small" style={{ filter: 'brightness(0)' }} />}
                        {isEdit ? 'Save Changes' : 'Add Doctor'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Delete Confirmation Modal
// ─────────────────────────────────────────────────────────────────────────────

const DeleteModal = ({ open, doctor, loading, onConfirm, onCancel }) => {
    if (!doctor) return null;

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            centered
            width={420}
            styles={{
                content: {
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 16,
                    padding: 0,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                },
            }}
        >
            <div style={{ padding: '28px 28px 24px' }}>

                {/* Title row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 11,
                        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <ExclamationCircleOutlined style={{ color: '#EF4444', fontSize: 18 }} />
                    </div>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 16 }}>
                        Remove Doctor
                    </span>
                </div>

                {/* Doctor pill */}
                <div style={{
                    background: 'var(--bg-base)', border: '1px solid var(--border-default)',
                    borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                    display: 'flex', alignItems: 'center', gap: 10,
                }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #79A7A5 0%, #4d8280 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color: '#0D0F14', flexShrink: 0,
                    }}>
                        {getInitials(doctor.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>
                            {doctor.name}
                        </div>
                        <div style={{ color: 'var(--text-secondary, #94A3B8)', fontSize: 11 }}>
                            {doctor.specialty}
                        </div>
                    </div>
                </div>

                <p style={{ color: 'var(--text-secondary, #94A3B8)', fontSize: 13, margin: '0 0 24px', lineHeight: 1.6 }}>
                    This will permanently remove {doctor.name} from the system and evict them from the doctors cache. This cannot be undone.
                </p>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        style={{
                            ...btnBase,
                            border: '1px solid var(--border-default)',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        style={{
                            ...btnBase,
                            background: '#EF4444',
                            color: '#fff',
                            fontWeight: 700,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        {loading && <Spin size="small" style={{ filter: 'brightness(0) invert(1)' }} />}
                        Remove
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Doctor Card
// ─────────────────────────────────────────────────────────────────────────────

const DoctorCard = ({ doctor, index, onEdit, onDelete }) => {
    const specialtyStyle = getSpecialtyStyle(doctor.specialty);

    return (
        <div
            style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-default)',
                borderRadius: 12,
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                transition: 'border-color 0.2s, box-shadow 0.2s',
                animation: 'fadeSlideIn 0.3s ease both',
                animationDelay: `${index * 0.04}s`,
            }}
            onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(121,167,165,0.5)';
                e.currentTarget.style.boxShadow  = '0 4px 20px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border-default)';
                e.currentTarget.style.boxShadow  = 'none';
            }}
        >
            {/* Avatar */}
            <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg, #79A7A5 0%, #4d8280 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, fontSize: 14, fontWeight: 700, color: '#0D0F14',
            }}>
                {getInitials(doctor.name)}
            </div>

            {/* Name */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{
                    color: 'var(--text-primary)', fontWeight: 600, fontSize: 14,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
                }}>
                    {doctor.name}
                </span>
            </div>

            {/* Specialty badge */}
            <span style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                letterSpacing: '0.6px', textTransform: 'uppercase', flexShrink: 0,
                background: specialtyStyle.bg, color: specialtyStyle.text, border: `1px solid ${specialtyStyle.border}`,
            }}>
                {doctor.specialty}
            </span>

            {/* Edit button */}
            <button
                onClick={() => onEdit(doctor)}
                title="Edit doctor"
                style={{
                    width: 34, height: 34, borderRadius: 8, cursor: 'pointer', fontSize: 14,
                    border: '1px solid rgba(121,167,165,0.3)',
                    background: 'rgba(121,167,165,0.08)',
                    color: '#79A7A5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s', flexShrink: 0,
                }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.25)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >
                <EditOutlined />
            </button>

            {/* Delete button */}
            <button
                onClick={() => onDelete(doctor)}
                title="Remove doctor"
                style={{
                    width: 34, height: 34, borderRadius: 8, cursor: 'pointer', fontSize: 14,
                    border: '1px solid rgba(239,68,68,0.25)',
                    background: 'rgba(239,68,68,0.06)',
                    color: '#EF4444',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s', flexShrink: 0,
                }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.25)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >
                <DeleteOutlined />
            </button>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

const DoctorsList = () => {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [formModal, setFormModal]     = useState({ open: false, mode: 'add', doctor: null });
    const [formLoading, setFormLoading] = useState(false);

    const [deleteModal, setDeleteModal]     = useState({ open: false, doctor: null });
    const [deleteLoading, setDeleteLoading] = useState(false);

    const { toasts, push: toast } = useToast();

    // ── Load doctors ────────────────────────────────────────────────────────
    const loadDoctors = useCallback(async () => {
        try {
            const result = await api_client.get_all_doctors();
            setDoctors(result.doctors || []);
        } catch (e) {
            console.error('Failed to load doctors', e);
            const msg = e?.response?.data?.error || 'Failed to load doctors. Please refresh.';
            toast(msg, 'error');
        }
    }, [toast]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await loadDoctors();
            setLoading(false);
        })();
    }, [loadDoctors]);

    // ── Search (client-side filter over the cached list) ───────────────────
    const filteredDoctors = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return doctors;
        return doctors.filter(d =>
            (d.name || '').toLowerCase().includes(q) ||
            (d.specialty || '').toLowerCase().includes(q)
        );
    }, [doctors, searchTerm]);

    // ── Add / Edit ──────────────────────────────────────────────────────────
    const openAdd  = () => setFormModal({ open: true, mode: 'add', doctor: null });
    const openEdit = (doctor) => setFormModal({ open: true, mode: 'edit', doctor });
    const closeForm = () => { if (!formLoading) setFormModal({ open: false, mode: 'add', doctor: null }); };

    const handleFormConfirm = async ({ name, specialty }) => {
        setFormLoading(true);
        try {
            if (formModal.mode === 'edit') {
                await api_client.update_doctor(formModal.doctor.id, name, specialty);
                toast(`${name} updated successfully.`);
            } else {
                await api_client.add_doctor(name, specialty);
                toast(`${name} added successfully.`);
            }
            await loadDoctors();
            setFormModal({ open: false, mode: 'add', doctor: null });
        } catch (e) {
            console.error('Save doctor failed', e);
            const msg = e?.response?.data?.error || 'Failed to save doctor. Please try again.';
            toast(msg, 'error');
        } finally {
            setFormLoading(false);
        }
    };

    // ── Delete ──────────────────────────────────────────────────────────────
    const openDelete  = (doctor) => setDeleteModal({ open: true, doctor });
    const closeDelete = () => { if (!deleteLoading) setDeleteModal({ open: false, doctor: null }); };

    const handleDeleteConfirm = async () => {
        const { doctor } = deleteModal;
        setDeleteLoading(true);
        try {
            await api_client.remove_doctor(doctor.id);
            await loadDoctors();
            toast(`${doctor.name} removed.`);
            setDeleteModal({ open: false, doctor: null });
        } catch (e) {
            console.error('Delete doctor failed', e);
            const msg = e?.response?.data?.error || 'Failed to remove doctor. Please try again.';
            toast(msg, 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    // ── Refresh cache ───────────────────────────────────────────────────────
    const handleRefreshCache = async () => {
        setRefreshing(true);
        try {
            await api_client.refresh_doctors_cache();
            await loadDoctors();
            toast('Doctors cache refreshed from database.');
        } catch (e) {
            console.error('Refresh cache failed', e);
            const msg = e?.response?.data?.error || 'Failed to refresh cache.';
            toast(msg, 'error');
        } finally {
            setRefreshing(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: '32px 28px', maxWidth: 860 }}>
            <style>{`
                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'flex-start',
                justifyContent: 'space-between', marginBottom: 20,
            }}>
                <div>
                    <h2 style={{ color: 'var(--text-primary)', fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>
                        Manage Doctors
                    </h2>
                    {!loading && (
                        <Text style={{ color: 'var(--text-secondary, #94A3B8)', fontSize: 13 }}>
                            {doctors.length} {doctors.length === 1 ? 'doctor' : 'doctors'} in system
                        </Text>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                        onClick={handleRefreshCache}
                        disabled={refreshing}
                        title="Refresh doctors cache from database"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '9px 14px', borderRadius: 9,
                            border: '1px solid var(--border-default)',
                            background: 'transparent', color: 'var(--text-primary)',
                            fontSize: 13, fontWeight: 600,
                            cursor: refreshing ? 'not-allowed' : 'pointer',
                            opacity: refreshing ? 0.6 : 1,
                            transition: 'opacity 0.15s',
                        }}
                    >
                        <ReloadOutlined spin={refreshing} style={{ fontSize: 14 }} />
                        Refresh
                    </button>

                    <button
                        onClick={openAdd}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '9px 16px', borderRadius: 9, border: 'none',
                            background: '#79A7A5', color: '#0D0F14',
                            fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                    >
                        <PlusOutlined style={{ fontSize: 15 }} />
                        Add Doctor
                    </button>
                </div>
            </div>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: 20 }}>
                <SearchOutlined style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--text-secondary, #94A3B8)', fontSize: 13,
                }} />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search doctors by name or specialty..."
                    style={{ ...inputStyle, padding: '9px 12px 9px 34px' }}
                    onFocus={e => e.target.style.borderColor = '#79A7A5'}
                    onBlur={e  => e.target.style.borderColor = 'var(--border-default)'}
                />
            </div>

            {/* Content */}
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
                    <Spin size="large" />
                </div>
            ) : filteredDoctors.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '60px 0',
                    color: 'var(--text-secondary, #94A3B8)', fontSize: 14,
                }}>
                    {searchTerm ? 'No doctors match your search.' : 'No doctors found.'}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {filteredDoctors.map((doctor, index) => (
                        <DoctorCard
                            key={doctor.id}
                            doctor={doctor}
                            index={index}
                            onEdit={openEdit}
                            onDelete={openDelete}
                        />
                    ))}
                </div>
            )}

            {/* Add / Edit modal */}
            <DoctorFormModal
                open={formModal.open}
                mode={formModal.mode}
                doctor={formModal.doctor}
                loading={formLoading}
                onConfirm={handleFormConfirm}
                onCancel={closeForm}
            />

            {/* Delete confirmation modal */}
            <DeleteModal
                open={deleteModal.open}
                doctor={deleteModal.doctor}
                loading={deleteLoading}
                onConfirm={handleDeleteConfirm}
                onCancel={closeDelete}
            />

            {/* Toasts */}
            <Toast toasts={toasts} />
        </div>
    );
};

export default DoctorsList;